import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { IacRepositoriesService } from "./iac-repositories.service";
import {
    LoadIacRepositories,
    LoadDataTableIacRepositories,
    UpdateCountersIacRepositories,
    ClearIacRepository,
    SetIacRepository,
    LoadIacRepositoriesCharts,
    LoadIacRepositoriesChartsFilteredData
} from "./iac-repositories.actions";
import { tap } from "rxjs";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { IacRepositoriesStateModel, IacRepository } from "./iac-repositories.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { CompliantRatioService } from "src/app/shared/service/compliant-ratio.service";
import { IacBadgeService } from "src/app/shared/service/iac-badge.service";
import { IacRepositoryTypeEnum } from "src/app/shared/enum/iac-repository-type.enum";
import { ChartComplianceRatioItem } from "src/app/shared/component/chart-compliance-ratio/chart-compliance-ratio.component";
import { ChartComplianceTotalItem } from "src/app/shared/component/chart-compliance-total/chart-compliance-total.component";
import { ChartVersusItem } from "src/app/shared/component/chart-versus/chart-versus.component";
import { DateService } from "src/app/shared/service/date.service";
import { IacRepositoryTypeLabelPipe } from "src/app/shared/pipe/iac-repository-type-label.pipe";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { MetaService } from "src/app/store/meta/meta.service";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";

export const IAC_TOKEN: StateToken<IacRepositoriesStateModel> = new StateToken<IacRepositoriesStateModel>('iacRepositories');

const IacRepositoriesStateModelDefault: IacRepositoriesStateModel = {
    repositories: [],
    compliantRatio: '0%',
    totalItems: '0',
    compliantItems: '0',
    notCompliantItems: '0',
    datatable: [],
    selectedRepository: {
        age: 0,
        id: '',
        cfstacks: [],
        tfstates: [],
        resources: [],
        issues: [],
        name: '',
        source: '',
        state: 'ignored',
        allBadges: [],
        checked: [],
        unchecked: [],
        ignored: [],
        tfmodules: [],
        challengers: [],
        archived: false,
        type: '',
        url: ''
    },
    chartsData: {
        measures: {}
    },
    filteredComplianceRatioChartData: {
        values: []
    },
    filteredComplianceTotalChartData: {
        values: []
    },
    filteredVersusChartData: {
        values: []
    }
}

@State<IacRepositoriesStateModel>({
    name: IAC_TOKEN,
    defaults: IacRepositoriesStateModelDefault
})
@Injectable()
export class IacRepositoriesState {
    private readonly KEY = IAC_TOKEN.getName()

    constructor(
        private iacRepositoriesService: IacRepositoriesService,
        private store: Store,
        private dateService: DateService,
        private iacRepositoryTypeLabelPipe: IacRepositoryTypeLabelPipe,
        private metaService: MetaService
    ) {
    }

    @Selector()
    static getRepositories(state: IacRepositoriesStateModel) {
        return state.repositories
    }

    @Selector()
    static getCompliantRatio(state: IacRepositoriesStateModel) {
        return state.compliantRatio
    }

    @Selector()
    static getTotalItems(state: IacRepositoriesStateModel) {
        return state.totalItems
    }

    @Selector()
    static getCompliantItems(state: IacRepositoriesStateModel) {
        return state.compliantItems
    }

    @Selector()
    static getNotCompliantItems(state: IacRepositoriesStateModel) {
        return state.notCompliantItems
    }

    @Selector()
    static getDatatable(state: IacRepositoriesStateModel) {
        return state.datatable
    }

    @Selector()
    static getSelectedRepository(state: IacRepositoriesStateModel) {
        return state.selectedRepository
    }

    @Selector()
    static getFilteredComplianceRatioChartData(state: IacRepositoriesStateModel) {
        return state.filteredComplianceRatioChartData
    }

    @Selector()
    static getFilteredComplianceTotalChartData(state: IacRepositoriesStateModel) {
        return state.filteredComplianceTotalChartData
    }

    @Selector()
    static getFilteredVersusChartData(state: IacRepositoriesStateModel) {
        return state.filteredVersusChartData
    }

    @Action(LoadIacRepositories)
    loadIacRepositories(ctx: StateContext<IacRepositoriesStateModel>) {
        
        if(!this.metaService.isStale(this.KEY)) return;

        return MetaRequestHelper.wrapRequest(
            this.store,
            this.KEY,
            this.iacRepositoriesService.loadIacGitlabRepositories(),
            value => {
                ctx.patchState({
                    repositories: value
                }),
                ctx.dispatch(
                    new LoadDataTableIacRepositories()
                )
            }
        )

    }

    @Action(LoadDataTableIacRepositories)
    loadDataTableIacRepositories(ctx: StateContext<IacRepositoriesStateModel>) {
        const state = ctx.getState()
        let datatable = state.repositories

        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.IAC_REPOSITORIES]]
        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                datatable = datatable.filter((repository) => repository['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                datatable = datatable.filter((repository) => {
                    let issuesTypeFound = false
                    repository.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                datatable = datatable.filter((repository) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = repository.age == filterAge
                    } else {
                        ageInRange = repository.age <= filterAge
                    }
                    return ageInRange
                })
            } else if (filter.key == 'challenger') {
                datatable = datatable.filter((repository) => {
                    let challengerFound = false
                    repository.challengers.forEach(challenger => {
                        challengerFound = challengerFound || challenger == filter.value
                    })
                    return challengerFound
                })
            } else if (filter.key == 'terraformVersion') {
                datatable = datatable.filter((repository) => {
                    return filter.value.includes(IacBadgeService.retrieveTerraformVersion(repository.allBadges))
                })
            } else if (filter.key == 'workspace') {
                datatable = datatable.filter((repository) => {
                    let workspaceFound = false
                    IacBadgeService.retrieveWorkspaces(repository.allBadges).forEach((workspace: any) => {
                        workspaceFound = workspaceFound || filter.value.includes(workspace)
                    });
                    return workspaceFound
                })
            } else if (filter.key == 'badge') {
                datatable = datatable.filter((repository) => {
                    let badgeFound = false
                    IacBadgeService.retrieveTerraformKeyValues(repository.allBadges).forEach((badgeKeyValue: any) => {
                        badgeFound = badgeFound || filter.value.includes(badgeKeyValue[0] + "=" + badgeKeyValue[1])
                    });
                    return badgeFound
                })
            } else {
                if (Array.isArray(filter.value)) {
                    datatable = datatable.filter((repository) => filter.value.includes(repository[filter.key as keyof IacRepository]))
                } else {
                    datatable = datatable.filter((repository) => repository[filter.key as keyof IacRepository] === filter.value)
                }
            }
        });
        ctx.patchState({
            datatable: datatable
        })
        ctx.dispatch(new UpdateCountersIacRepositories())
    }

    @Action(UpdateCountersIacRepositories)
    updateCountersIacRepositories(ctx: StateContext<IacRepositoriesStateModel>) {
        const state = ctx.getState()
        let totalItemsCounter = 0
        let compliantItemsCounter = 0
        let notCompliantItemsCounter = 0
        state.datatable.forEach(repository => {
            if (repository.state == 'compliant') {
                totalItemsCounter++;
                compliantItemsCounter++;
            } else if (repository.state == 'not_compliant') {
                totalItemsCounter++;
                notCompliantItemsCounter++;
            }
        });
        ctx.patchState({
            compliantRatio: CompliantRatioService.calculateCompliantRatio(compliantItemsCounter, notCompliantItemsCounter),
            totalItems: '' + totalItemsCounter,
            compliantItems: '' + compliantItemsCounter,
            notCompliantItems: '' + notCompliantItemsCounter
        })
    }

    @Action(ClearIacRepository)
    clearIacRepository(ctx: StateContext<IacRepositoriesStateModel>) {
        return ctx.patchState({
            selectedRepository: IacRepositoriesStateModelDefault.selectedRepository
        })
    }

    @Action(SetIacRepository)
    setIacRepository(ctx: StateContext<IacRepositoriesStateModel>, payload: any) {
        return this.iacRepositoriesService.loadIacGitlabRepositoryDetails(payload.payload)
            .pipe(
                tap(value => {
                    ctx.patchState({
                        selectedRepository: value
                    })
                }),
            )
    }

    @Action(LoadIacRepositoriesCharts)
    loadIacRepositoriesCharts(ctx: StateContext<IacRepositoriesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig);
        const { start_date, end_date } = DateService.getChartsMeasuresDateRange(chartsConfig);

        const CHART_KEY = this.KEY + "-charts-" + start_date + "-" + end_date + "-" + chartsConfig.granularity

        if(!this.metaService.isStale(CHART_KEY)) return;

        return MetaRequestHelper.wrapRequest(
            this.store,
            CHART_KEY,
            this.iacRepositoriesService.loadIacGitlabRepositoriesMeasures(start_date, end_date, chartsConfig.granularity),
            charts => {
                ctx.patchState({
                    chartsData: charts
                }),
                ctx.dispatch(new LoadIacRepositoriesChartsFilteredData())
            }
        )
    }

    @Action(LoadIacRepositoriesChartsFilteredData)
    loadIacRepositoriesChartsFilteredData(ctx: StateContext<IacRepositoriesStateModel>) {
        let chartsData = ctx.getState().chartsData
        let complianceRatioChartValues: ChartComplianceRatioItem[] = [];
        let complianceTotalChartValues: ChartComplianceTotalItem[] = [];
        let complianceVersusChartValues: ChartVersusItem[] = [];
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.IAC_MEASURES]]
        let types: string[] = []
        filters.forEach(filter => {
            types = filter.value
        })
        if (types.length == 0) {
            types = Object.keys(IacRepositoryTypeEnum)
        }
        for (const dateString in chartsData.measures) {
            const dateMeasures = chartsData.measures[dateString];
            let formattedDate = this.dateService.formatLongDate(dateString)
            let compliantCount = 0
            let notCompliantCount = 0
            for (const type in dateMeasures) {
                if (type != IacRepositoryTypeEnum[IacRepositoryTypeEnum.training] && types.includes(type)) {
                    const typeMeasures = dateMeasures[type];
                    compliantCount += typeMeasures.checked;
                    notCompliantCount += typeMeasures.unchecked;
                }
            }
            complianceRatioChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "value": CompliantRatioService.calculateCompliantRatio(compliantCount, notCompliantCount)
            });
            complianceTotalChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "compliantCount": compliantCount,
                "notCompliantCount": notCompliantCount,
                "total": compliantCount + notCompliantCount,
                "delta": Math.abs(compliantCount - notCompliantCount)
            });
        }
        let lastDate = Object.keys(chartsData.measures)[Object.keys(chartsData.measures).length - 1]
        let lastMeasure = chartsData.measures[lastDate]
        for (const type in lastMeasure) {
            if (type != IacRepositoryTypeEnum[IacRepositoryTypeEnum.training] && types.includes(type)) {
                let typeMeasure = lastMeasure[type]
                let colorKey = 'charts_color_' + complianceVersusChartValues.length
                let dateData = {
                    "type": this.iacRepositoryTypeLabelPipe.transform(type),
                    "count": typeMeasure.checked + typeMeasure.unchecked + typeMeasure.ignored,
                    "color": ColorEnum[colorKey as keyof typeof ColorEnum]
                };
                complianceVersusChartValues.push(dateData);
            }
        }
        return ctx.patchState({
            filteredComplianceRatioChartData: {
                values: complianceRatioChartValues
            },
            filteredComplianceTotalChartData: {
                values: complianceTotalChartValues
            },
            filteredVersusChartData: {
                values: complianceVersusChartValues
            }
        })
    }
}
